import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useBackgroundTasks } from "../contexts/BackgroundTaskContext";
import { useSubscription } from "../contexts/SubscriptionContext";
import { TasksDropdown } from "./TasksDropdown";
import { SafeLink } from "./SafeLink";
import { getUserAccessStatus } from "../utils";

const Navbar: React.FC = () => {
    const { firebaseUser, dbUser, logout } = useAuth();
    const navigate = useNavigate();
    const { getActiveTasksCount, getUnviewedTasksCount } = useBackgroundTasks();
    const { openSubscriptionModal } = useSubscription();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
    const userDropdownRef = useRef<HTMLDivElement>(null);

    const activeTasksCount = getActiveTasksCount();
    const unviewedTasksCount = getUnviewedTasksCount();
    const totalNotifications = activeTasksCount + unviewedTasksCount;
    
    // Get user access status
    const accessStatus = getUserAccessStatus(dbUser);
    const { canGetFreeTrial, hasFreeRuns, freeRunsRemaining } = accessStatus;

    const handleLogout = async () => {
        try {
            await logout();
            navigate("/");
        } catch (error) {
            console.error("Error logging out:", error);
        }
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
                setIsUserDropdownOpen(false);
            }
        };

        if (isUserDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isUserDropdownOpen]);

    return (
        <nav className="bg-white shadow-lg sticky top-0 z-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex justify-between h-16">
                    {/* Logo and main nav */}
                    <div className="flex">
                        <div className="flex-shrink-0 flex items-center">
                            <Link
                                to="/"
                                className="text-xl font-bold text-primary-600"
                            >
                                MealPlan
                            </Link>
                        </div>

                        {/* Desktop navigation */}
                        <div className="hidden md:ml-6 md:flex md:space-x-8">
                            <SafeLink
                                to="/"
                                className="border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium"
                            >
                                Home
                            </SafeLink>

                            {/* Public pages */}
                            <SafeLink
                                to="/about"
                                className="border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium"
                            >
                                About
                            </SafeLink>

                            {/* Protected pages - only show when authenticated */}
                            {firebaseUser && (
                                <>
                                    <SafeLink
                                        to="/meal-planner/generate"
                                        className="border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium"
                                    >
                                        Meal Planner
                                    </SafeLink>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Auth buttons */}
                    <div className="hidden md:ml-6 md:flex md:items-center md:space-x-4">
                        {firebaseUser ? (
                            <>
                                {/* Background Tasks Indicator */}
                                <TasksDropdown
                                    activeTasksCount={activeTasksCount}
                                    unviewedTasksCount={unviewedTasksCount}
                                    totalNotifications={totalNotifications}
                                />

                                {/* User menu */}
                                <div className="flex items-center space-x-4">
                                    <div className="relative" ref={userDropdownRef}>
                                        <button
                                            onClick={() => setIsUserDropdownOpen(!isUserDropdownOpen)}
                                            className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-900 hover:text-gray-700 text-sm font-semibold transition-colors duration-200"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                            <span>{dbUser?.first_name || firebaseUser.displayName || "User"}</span>
                                            <svg className={`w-4 h-4 transition-transform duration-200 ${isUserDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>

                                        {/* Dropdown menu */}
                                        {isUserDropdownOpen && (
                                            <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                                                {/* User email label */}
                                                {(firebaseUser?.email || dbUser?.email) && (
                                                    <div className="px-4 py-2 border-b border-gray-100">
                                                        <div 
                                                            className="text-xs text-gray-500 truncate pr-2"
                                                            title={firebaseUser?.email || dbUser?.email}
                                                        >
                                                            {firebaseUser?.email || dbUser?.email}
                                                        </div>
                                                    </div>
                                                )}
                                                <Link
                                                    to="/profile/profiles"
                                                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    onClick={() => setIsUserDropdownOpen(false)}
                                                >
                                                    <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                                    </svg>
                                                    Profiles
                                                </Link>
                                                <Link
                                                    to="/profile/config"
                                                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    onClick={() => setIsUserDropdownOpen(false)}
                                                >
                                                    <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                    </svg>
                                                    AI Config
                                                </Link>
                                                <Link
                                                    to="/profile/billing"
                                                    className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                    onClick={() => setIsUserDropdownOpen(false)}
                                                >
                                                    <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                                    </svg>
                                                    Billing
                                                </Link>
                                                <div className="border-t border-gray-100"></div>
                                                <button
                                                    onClick={() => {
                                                        handleLogout();
                                                        setIsUserDropdownOpen(false);
                                                    }}
                                                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                                >
                                                    <svg className="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                                    </svg>
                                                    Sign Out
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Subscription status badge */}
                                    {dbUser && (
                                        <span
                                            className={`px-2 py-1 text-xs rounded-full ${
                                                dbUser.passioapikey || dbUser.subscription_status === 'premium'
                                                    ? "bg-primary-100 text-primary-800"
                                                    : "bg-gray-100 text-gray-800"
                                            }`}
                                        >
                                            {dbUser.subscription_status === 'trial' && hasFreeRuns 
                                                ? `free (${freeRunsRemaining})`
                                                : dbUser.subscription_status}
                                        </span>
                                    )}

                                    {!dbUser?.passioapikey && dbUser?.subscription_status !== 'premium' && (
                                        <button
                                            onClick={openSubscriptionModal}
                                            className={`${canGetFreeTrial ? 'bg-green-500 hover:bg-green-600' : 'bg-primary-500 hover:bg-primary-600'} text-white px-3 py-2 rounded-md text-sm font-medium`}
                                        >
                                            {canGetFreeTrial ? 'Free Trial' : 'Upgrade'}
                                        </button>
                                    )}
                                </div>
                            </>
                        ) : (
                            <>
                                <Link
                                    to="/signin"
                                    className="text-gray-500 hover:text-gray-700 px-3 py-2 rounded-md text-sm font-medium"
                                >
                                    Sign In
                                </Link>
                                <Link
                                    to="/signup"
                                    className="bg-primary-500 hover:bg-primary-600 text-white px-3 py-2 rounded-md text-sm font-medium"
                                >
                                    Sign Up
                                </Link>
                            </>
                        )}
                    </div>

                    {/* Mobile menu button */}
                    <div className="md:hidden flex items-center">
                        <button
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
                        >
                            <svg
                                className="h-6 w-6"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 6h16M4 12h16M4 18h16"
                                />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile menu */}
            {isMenuOpen && (
                <div className="md:hidden">
                    <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-gray-50">
                        <SafeLink
                            to="/"
                            className="text-gray-700 hover:text-gray-900 block px-3 py-2 rounded-md text-base font-medium"
                            onClick={() => setIsMenuOpen(false)}
                        >
                            Home
                        </SafeLink>
                        <SafeLink
                            to="/about"
                            className="text-gray-700 hover:text-gray-900 block px-3 py-2 rounded-md text-base font-medium"
                            onClick={() => setIsMenuOpen(false)}
                        >
                            About
                        </SafeLink>

                        {firebaseUser ? (
                            <>
                                <SafeLink
                                    to="/meal-planner/generate"
                                    className="text-gray-700 hover:text-gray-900 block px-3 py-2 rounded-md text-base font-medium"
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    Meal Planner
                                </SafeLink>
                                <SafeLink
                                    to="/profile"
                                    className="text-gray-700 hover:text-gray-900 block px-3 py-2 rounded-md text-base font-medium"
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    Profile
                                </SafeLink>
                                {!dbUser?.passioapikey && dbUser?.subscription_status !== 'premium' && (
                                    <button
                                        onClick={() => {
                                            openSubscriptionModal();
                                            setIsMenuOpen(false);
                                        }}
                                        className={`${canGetFreeTrial ? 'bg-green-500 text-white' : 'bg-primary-500 text-white'} hover:opacity-90 block px-3 py-2 rounded-md text-base font-medium w-full text-left`}
                                    >
                                        {canGetFreeTrial ? 'Free Trial' : 'Upgrade'}
                                    </button>
                                )}
                                <button
                                    onClick={() => {
                                        handleLogout();
                                        setIsMenuOpen(false);
                                    }}
                                    className="text-gray-700 hover:text-gray-900 block px-3 py-2 rounded-md text-base font-medium w-full text-left"
                                >
                                    Sign Out
                                </button>
                            </>
                        ) : (
                            <>
                                <Link
                                    to="/signin"
                                    className="text-gray-700 hover:text-gray-900 block px-3 py-2 rounded-md text-base font-medium"
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    Sign In
                                </Link>
                                <Link
                                    to="/signup"
                                    className="text-gray-700 hover:text-gray-900 block px-3 py-2 rounded-md text-base font-medium"
                                    onClick={() => setIsMenuOpen(false)}
                                >
                                    Sign Up
                                </Link>
                            </>
                        )}
                    </div>
                </div>
            )}
        </nav>
    );
};

export default Navbar;
